import {ExternalLinkIcon} from '@heroicons/react/outline';
import {DocumentDuplicateIcon} from '@heroicons/react/solid';
import {isEmpty} from 'lodash-es';
import type {ReactElement} from 'react';
import React from 'react';
import {Link} from 'react-router-dom';

import {AfriexAdminPermission} from '@afriex/core/domain/enums';

import {Button} from '@/components';
import {PermissionsProvider} from '@/components/common/PermissionsProvider';
import {IPDetailsLink} from '@/components/transactions/Detail';
import useKYCDetails from '@/screens/KYC/Details/useKYCDetails';
import VirtualAccountInfo from '@/screens/KYC/Details/VirtualAccountInfo';
import type {AdminAfriexPerson} from '@/types';
import {
  capitalizeWords,
  formatValues,
  getEarliestLocation,
} from '@/utils/dashboard';
import * as formatDate from '@/utils/date';
import {getClarityUserSessionURL} from '@/utils/getUserSessionURLs';
import {copyToClipboard} from '@/utils/user';

export interface UserProfileProps {
  user: AdminAfriexPerson;
  type?: 'user' | 'kyc';
  showUserID?: boolean;
  showPhone?: boolean;
  showPhoneVerified?: boolean;
  showReferredBy?: boolean;
  showSecurityEnabled?: boolean;
  showKYCStatus?: boolean;
  showUsername?: boolean;
  showDateOpened?: boolean;
  showDeviceId?: boolean;
  showDeviceType?: boolean;
  showAppVersion?: boolean;
  showLastLogin?: boolean;
  showEmail?: boolean;
  showViewTransactions?: boolean;
  showVirtualAccount?: boolean;
  showStripeUser?: boolean;
  showRewardPoints?: boolean;
  showInteracEmails?: boolean;
  showRegistrationIP?: boolean;
  showKycProcessors?: boolean;
  showUserSession?: boolean;
  showUserLogs?: boolean;
}

const EMAIL_DISPLAY_LENGTH = 15;

// The full value stays available through the copy button, so the label only
// needs to fit the column.
const formatEmailForDisplay = (email: string | undefined) => {
  if (!email) {
    return '-';
  }
  return email.length > EMAIL_DISPLAY_LENGTH
    ? `${email.slice(0, EMAIL_DISPLAY_LENGTH)}...`
    : email;
};

const UserProfile = ({
  user,
  type = 'user',
  showUserID,
  showPhone,
  showPhoneVerified,
  showReferredBy,
  showSecurityEnabled,
  showKYCStatus,
  showUsername,
  showDateOpened,
  showDeviceId,
  showDeviceType,
  showAppVersion,
  showLastLogin,
  showEmail,
  showViewTransactions,
  showVirtualAccount,
  showStripeUser,
  showRewardPoints,
  showInteracEmails,
  showRegistrationIP,
  showKycProcessors,
  showUserSession,
  showUserLogs,
}: UserProfileProps): ReactElement => {
  const {virtualAccountInfo} = useKYCDetails(user?.id);
  const interac = user.externalAccounts?.juicyway;
  const earliestLocation = getEarliestLocation(user.locations);
  const verificationKeys = Object.keys(user?.kyc?.verification ?? {});
  const isStripeUserAccountValid =
    user?.externalAccounts?.stripe &&
    Object.keys(user.externalAccounts.stripe).length > 0 &&
    Object.values(user.externalAccounts.stripe).every((value) => !!value);
  const userCurrentDevice =
    user.currentDeviceInfo ??
    (Array.isArray(user.devices) && user.devices.filter(Boolean).length > 0
      ? [...user.devices].filter(Boolean).sort((a, b) => {
          const dateA = a.updatedAt ? new Date(a.updatedAt).getTime() : 0;
          const dateB = b.updatedAt ? new Date(b.updatedAt).getTime() : 0;
          return dateB - dateA; // descending
        })[0]
      : null);
  const userCurrentDeviceId = userCurrentDevice
    ? userCurrentDevice.deviceId
    : 'N/A';
  const userCurrentDeviceType = userCurrentDevice
    ? userCurrentDevice.deviceType
    : 'N/A';
  const userCurrentAppVersion = userCurrentDevice
    ? userCurrentDevice.appVersion
    : 'N/A';
  const userEmail = user?.currentEmail;
  return (
    <div>
      {/* Email */}
      {showEmail && (
        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
          <p className="flex-[0.4] text-slate-500 text-sm dark:text-slate-100">
            Account Email
          </p>
          <div className="flex-[0.6] text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100">
            <div className="flex items-center gap-2 truncate">
              <span className="truncate">
                {formatEmailForDisplay(userEmail)}
              </span>
              {userEmail && (
                <span
                  className="truncate"
                  title="click to copy email"
                  onClick={() => copyToClipboard(userEmail)}
                >
                  <DocumentDuplicateIcon className="w-4 h-4 text-cyan-700 cursor-pointer" />
                </span>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Username */}
      {showUsername && (
        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 dark:bg-gray-900 justify-between">
          <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
            Account Username
          </p>
          <p className="flex-[0.6] text-sm font-medium text-gray-700 w-1/2 dark:text-gray-100 truncate">
            {user.name?.userName || '-'}
          </p>
        </div>
      )}

      {/* User ID */}
      {showUserID && (
        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
          <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
            User ID
          </p>
          <div className="flex-[0.6] flex flex-row text-sm font-medium text-gray-700 gap-2 w-1/2 dark:text-gray-100 ">
            <div className="flex items-center gap-2 truncate">
              <span className="truncate">{user.id}</span>
              <span
                title="click to copy userID"
                onClick={() => copyToClipboard(user.id)}
              >
                <DocumentDuplicateIcon className="w-4 h-4 text-cyan-700 cursor-pointer" />
              </span>
            </div>
            {type !== 'user' && (
              <Link
                to={`/users/${user.id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button variant="ghost" className="no-padding no-margin">
                  View User Profile
                </Button>
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Phone */}
      {showPhone && (
        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
          <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
            Phone Number
          </p>
          <p className="flex-[0.6] text-sm font-medium text-gray-700  w-1/2 dark:text-gray-100 ">
            {user?.currentPhone || '-'}
          </p>
        </div>
      )}

      {/* Phone Verified */}
      {showPhoneVerified && (
        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
          <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
            Phone Verified
          </p>
          <p className="flex-[0.6] text-sm font-medium text-gray-700  w-1/2 dark:text-gray-100 ">
            {user?.isPhoneVerified ? 'Verified' : 'Not Verified'}
          </p>
        </div>
      )}

      {/* Referred By */}
      {showReferredBy && (
        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
          <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
            Referred By
          </p>
          {user?.referralInfo?.referrerId ? (
            <p className="flex-[0.6] text-sm font-medium text-gray-700  w-1/2 dark:text-gray-100 ">
              {user?.referralInfo?.referrerId} (
              {user?.referralInfo?.referrerUserName})
            </p>
          ) : (
            <p className="flex-[0.6] text-sm font-medium text-gray-700  w-1/2 dark:text-gray-100 ">
              None
            </p>
          )}
        </div>
      )}

      {/* Security Enabled */}
      {showSecurityEnabled && (
        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
          <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
            Security Enabled
          </p>
          <p className="flex-[0.6] text-sm font-medium text-gray-700  w-1/2 dark:text-gray-100 ">
            {user.isSecurityFlagged ? 'True' : 'False'}
          </p>
        </div>
      )}

      {/* KYC Status */}
      {showKYCStatus && (
        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
          <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
            KYC Status
          </p>
          <p className="flex-[0.6] text-sm font-medium text-gray-700  w-1/2 dark:text-gray-100 ">
            <span className="mr-3">{user?.kyc?.status?.toUpperCase()}</span>
            {type !== 'kyc' && (
              <Link
                to={`/kyc/${user.id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button variant="ghost" className="no-padding no-margin">
                  View KYC details
                </Button>
              </Link>
            )}
          </p>
        </div>
      )}

      {showKycProcessors && verificationKeys?.length > 0 && (
        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
          <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
            KYC Processors
          </p>
          <p className="flex-[0.6] text-sm font-medium text-gray-700  w-1/2 dark:text-gray-100 ">
            {capitalizeWords(verificationKeys.join(', '))}
          </p>
        </div>
      )}

      {/* Date Opened */}
      {showDateOpened && (
        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
          <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
            Date Opened
          </p>
          <p className="flex-[0.6] text-sm font-medium text-gray-700  w-1/2 dark:text-gray-100 ">
            {formatDate.DDMonYYYY(user?.createdAt?.toString() || '')}
          </p>
        </div>
      )}

      {/* Device ID */}
      {showDeviceId && (
        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
          <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
            Device ID
          </p>
          <p className="flex-[0.6] text-sm font-medium text-gray-700  w-1/2 dark:text-gray-100 ">
            {userCurrentDeviceId}
          </p>
        </div>
      )}

      {/* Device Type */}
      {showDeviceType && (
        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
          <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
            Device Type
          </p>
          <p className="flex-[0.6] text-sm font-medium text-gray-700  w-1/2 dark:text-gray-100 ">
            {userCurrentDeviceType}
          </p>
        </div>
      )}

      {showAppVersion && (
        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
          <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
            App version
          </p>
          <p className="flex-[0.6] text-sm font-medium text-gray-700  w-1/2 dark:text-gray-100 ">
            {userCurrentAppVersion}
          </p>
        </div>
      )}
      {/* Registration IP */}
      {showRegistrationIP && earliestLocation?.ipAddress && (
        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
          <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
            Registration IP Address
          </p>
          <p className="flex-[0.6] text-sm font-medium text-gray-700  w-1/2 dark:text-gray-100 truncate">
            {earliestLocation?.ipAddress}{' '}
            <IPDetailsLink ipAddress={earliestLocation?.ipAddress} />
          </p>
        </div>
      )}

      {/* Last Login */}
      {showLastLogin &&
        (user.lastLoggedInAt ? (
          <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
            <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
              Last Login
            </p>
            <p className="flex-[0.6] text-sm font-medium text-gray-700  w-1/2 dark:text-gray-100 ">
              {formatDate.dateTimeInTimeZone(user.lastLoggedInAt)}
            </p>
          </div>
        ) : null)}

      {/* Microsoft Clarity Link */}
      {showUserSession && (
        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
          <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
            Clarity User Session
          </p>
          <p className="flex-[0.6] text-sm font-medium text-gray-700  w-1/2 dark:text-gray-100 ">
            <Link
              to={getClarityUserSessionURL(user.id)}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center text-sm text-cyan-700 gap-2"
            >
              <ExternalLinkIcon className="w-4 h-4" />
              View Session
            </Link>
          </p>
        </div>
      )}

      {/* User Logs */}
      {showUserLogs && (
        <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
          <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
            Datadog Logs
          </p>
          <p className="flex-[0.6] text-sm font-medium text-gray-700  w-1/2 dark:text-gray-100 ">
            <Link
              to={`/user-logs/${user.id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center text-sm text-cyan-700 gap-2"
            >
              <ExternalLinkIcon className="w-4 h-4" />
              View Logs
            </Link>
          </p>
        </div>
      )}

      {/* View Transactions */}
      <PermissionsProvider
        permission={AfriexAdminPermission.UserTransactionRead}
      >
        {showViewTransactions && (
          <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
            <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
              Transactions
            </p>
            <p className="flex-[0.6] text-sm font-medium text-gray-700  w-1/2 dark:text-gray-100 ">
              <Link
                to={`/transactions/user/${user.id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button variant="ghost" className="no-padding no-margin">
                  View Transactions
                </Button>
              </Link>
            </p>
          </div>
        )}
      </PermissionsProvider>

      {showInteracEmails && interac?.emails && interac?.emails?.length > 0 && (
        <div className="mb-2 py-2 px-3 flex gap-3 bg-gray-100 justify-between dark:bg-gray-900">
          <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
            Interac Linked Emails
          </p>
          <p className="flex-[0.6] text-sm font-medium text-gray-700  w-1/2 dark:text-gray-100 overflow-hidden whitespace-normal  truncate">
            {interac.emails.join(', ')}
          </p>
        </div>
      )}

      {showRewardPoints &&
        user?.rewardInfo &&
        (user.rewardInfo.rewardPoints ?? 0) > 0 && (
          <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
            <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
              Reward Tier
            </p>
            <p className="flex flex-[0.6] text-sm font-medium text-gray-700  w-1/2 dark:text-gray-100 gap-2 break-all">
              <span>{capitalizeWords(user.rewardInfo.rewardTier ?? '')}</span>
              <span className="text-sm font-semibold text-blue-700">
                (
                {formatValues(
                  Number(user.rewardInfo.rewardPoints),
                  'money',
                  3,
                ) + ' Points'}
                )
              </span>
              <PermissionsProvider
                permission={AfriexAdminPermission.TransactionRewardedRead}
              >
                <Link
                  to={`/transactions/rewards/${user.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="ghost" className="no-padding no-margin">
                    View Reward Transactions
                  </Button>
                </Link>
              </PermissionsProvider>
            </p>
          </div>
        )}

      {showStripeUser && isStripeUserAccountValid && (
        <React.Fragment>
          <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
            <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
              Stripe Customer ID
            </p>
            <p className="flex-[0.6] text-sm font-medium text-gray-700  w-1/2 dark:text-gray-100  truncate">
              {user?.externalAccounts?.stripe?.customers?.default || '-'}
            </p>
          </div>

          <div className="mb-2 py-2 px-3 flex items-center gap-3 bg-gray-100 justify-between dark:bg-gray-900">
            <p className="flex-[0.4] text-slate-500 text-sm  dark:text-slate-100">
              Stripe Account ID
            </p>
            <p className="flex-[0.6] text-sm font-medium text-gray-700  w-1/2 dark:text-gray-100  truncate">
              {user?.externalAccounts?.stripe?.accounts?.default || '-'}
            </p>
          </div>
        </React.Fragment>
      )}

      {showVirtualAccount && !isEmpty(virtualAccountInfo) && (
        <VirtualAccountInfo data={virtualAccountInfo} />
      )}
    </div>
  );
};

export default UserProfile;
